import { ConstructionAdderType, ProgramType } from 'types';

export const ENERGY_BACKUP_CAP_SCHEDULES_FLAG = 'energyBackupCapSchedules';

/** Mirrors the API schema bound on `caps`; enforced silently since nobody should get near it. */
export const MAX_CAP_ROWS = 50;

export const CAP_SCHEDULE_ADDER_TYPES: readonly string[] = [
  ConstructionAdderType.backupBatteryPriceCap,
  ConstructionAdderType.arbitrageBatteryPriceCap,
];

export const supportsCapSchedules = (programType?: string, adderType?: string): boolean =>
  programType === ProgramType.energyBackup && !!adderType && CAP_SCHEDULE_ADDER_TYPES.includes(adderType);

const CAP_SCHEDULE_PAGE_TITLES: Record<string, string> = {
  [ConstructionAdderType.backupBatteryPriceCap]: 'Create Backup Battery Cap Schedule',
  [ConstructionAdderType.arbitrageBatteryPriceCap]: 'Create Arbitrage Battery Cap Schedule',
};

export const capSchedulePageTitle = (adderType?: string): string =>
  (adderType && CAP_SCHEDULE_PAGE_TITLES[adderType]) || 'Create Cap Schedule';

export const priceCapsListPath = (programType: string, priceCapType: string) =>
  `/admin/pricing/${programType}/price-caps/${priceCapType}`;

export const createCapSchedulePath = (programType: string, priceCapType: string) =>
  `${priceCapsListPath(programType, priceCapType)}/schedules/create`;

/** Client shape of `EnergyBackupCapSchedule`; ids arrive as strings over the wire. */
export interface CapSchedule {
  productLineId: string;
  caps: number[];
  electricalAdder?: number;
}

const wholeDollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export const ordinal = (n: number): string => {
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

/** The last cap applies to every battery after it, so its label says so. */
export const batteryRowLabel = (index: number, rowCount: number): string => {
  const label = `${ordinal(index + 1)} battery`;
  return index === rowCount - 1 ? `${label} and beyond` : label;
};

/** e.g. "Powerwall 3: 1st $22,000 · 2nd $17,000 · 3rd+ $11,000" */
export const formatCapSchedule = (schedule: CapSchedule, productLineName?: string): string => {
  const caps = schedule.caps
    .map((cap, index) => {
      const suffix = index === schedule.caps.length - 1 ? '+' : '';
      return `${ordinal(index + 1)}${suffix} ${wholeDollarFormatter.format(cap)}`;
    })
    .join(' · ');
  const electrical = schedule.electricalAdder
    ? ` · electrical ${wholeDollarFormatter.format(schedule.electricalAdder)}`
    : '';
  return `${productLineName ?? schedule.productLineId}: ${caps}${electrical}`;
};

const isBlank = (value: string) => value.trim() === '';

export const validateCapAmount = (value: string): true | string => {
  if (isBlank(value)) return 'Cap is required';
  const amount = Number(value);
  if (Number.isNaN(amount)) return 'Must be a number';
  if (amount < 0) return 'Must be 0 or more';
  return Number.isInteger(amount) || 'Must be a whole dollar amount';
};

/** Optional: blank means no electrical adder, which contributes nothing to the allowable total. */
export const validateElectricalAdder = (value: string): true | string => isBlank(value) || validateCapAmount(value);

/** Blank is omitted rather than sent as 0 or null, so the API stores no adder. */
export const electricalAdderPayload = (value: string): { electricalAdder?: number } =>
  isBlank(value) ? {} : { electricalAdder: Number(value) };

/** Lines the API rejected as inactive or missing, so their sections can be flagged. */
export const rejectedProductLineIds = (error: unknown): string[] => {
  const ids = (error as { data?: { additionalContext?: { productLineIds?: unknown } } } | undefined)?.data
    ?.additionalContext?.productLineIds;
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
};
